"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { SANCTION, composerDeclaration, valider } from "@/lib/tools/nonConvictionDeclaration";
import { dateEnToutesLettres, documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Ecran,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * Le premier formulaire de la rubrique, et le patron des dix qui suivent.
 *
 * Un simulateur calcule, un formulaire recopie : la question n'est plus « que
 * dit la règle » mais « qu'est-ce qui doit figurer sur la feuille ». Le format
 * du parcours ne change pas pour autant, parce que c'est lui qui empêche la
 * page de ressembler aux quinze champs empilés d'un site administratif.
 *
 * RIEN NE SORT DU NAVIGATEUR. Ni requête, ni stockage sur l'appareil : l'état
 * civil et la filiation vivent dans le state du composant, la signature dans le
 * stockage de session (voir SignaturePad.jsx, qui la reprend d'un formulaire à
 * l'autre), le PDF se fabrique ici, et fermer l'onglet efface tout. C'est la
 * promesse affichée en tête du parcours, et elle serait fausse au premier
 * fetch.
 *
 * Le document reste en français quand l'interface est en anglais : c'est une
 * pièce d'un dossier français. L'anglophone reçoit la traduction de ce qu'il
 * signe, sous l'aperçu, pas un document que le guichet refusera.
 */

const L = {
    fr: {
        phases: ["Vous", "Votre filiation", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le document s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        identiteTitre: "Commençons par votre état civil",
        identiteAide: "Reprenez l'orthographe exacte de votre acte de naissance, accents compris.",
        madame: "Madame",
        civilite: "Civilité",
        monsieur: "Monsieur",
        civiliteAide: "Sert à accorder la déclaration, qui s'écrit à la première personne.",
        champPrenoms: "Vos prénoms",
        aidePrenoms: "Tous vos prénoms, dans l'ordre de l'acte de naissance.",
        champNom: "Votre nom",
        aideNom: "Votre nom de naissance, même si vous portez un nom d'usage.",
        naissanceTitre: "Où et quand êtes-vous né ?",
        naissanceAide: "Avec le nom de vos parents, ces deux informations servent à vous identifier.",
        champDateNaissance: "Date de naissance",
        champVilleNaissance: "Commune de naissance",
        champPaysNaissance: "Pays de naissance",
        aidePaysNaissance: "À remplir seulement si vous êtes né hors de France.",
        domicileTitre: "Où habitez-vous ?",
        domicileAide: "Votre adresse personnelle, celle de votre domicile, pas celle de votre activité si elle diffère.",
        champVoie: "Numéro et voie",
        champComplement: "Complément d'adresse",
        aideComplement: "Facultatif. Bâtiment, étage, lieu-dit.",
        champCodePostal: "Code postal",
        champCommune: "Commune",
        filiationTitre: "Qui sont vos parents ?",
        filiationAide: "C'est la partie « filiation » du document. Recopiez les noms tels qu'ils figurent sur votre acte de naissance.",
        pere: "Votre père",
        mere: "Votre mère",
        champPrenomsParent: "Prénoms",
        champNomPere: "Nom",
        champNomMere: "Nom de famille",
        aideNomMere: "Son nom de naissance, pas son nom d'épouse.",
        pereInconnu: "Mon acte de naissance ne le mentionne pas",
        mereInconnue: "Mon acte de naissance ne la mentionne pas",
        signatureTitre: "Où signez-vous, et quand ?",
        signatureAide: "La date compte : une déclaration antérieure à votre dossier passe, l'inverse fait tiquer.",
        champLieuSignature: "Lieu de signature",
        champDateSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",
        continuer: "Continuer",
        voirDocument: "Voir mon document",
        resultatTitre: "Votre déclaration",
        apercu: "Aperçu du document",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",
        erreurDateLue: "Nous lisons {date}. Vérifiez l'année.",
        erreurFormat: "Ce format n'est pas reconnu.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais leur contenu ne passe pas. Corrigez-les et revenez ici.",
        motifFormat: "le format n'est pas reconnu",
        motifAgeImpossible: "l'année saisie donne un âge impossible, vérifiez-la",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre document.",
        alerteMineur: "Vous êtes mineur d'après cette date de naissance. La déclaration reste possible si vous êtes émancipé, sinon votre dossier sera refusé.",
        suiteTitre: "Et maintenant",
        suite1: "Imprimez le document, signez-le à la main si vous ne l'avez pas signé ici, puis scannez-le ou photographiez-le.",
        suite2: "Joignez-le à votre formalité de création sur le guichet unique, avec votre pièce d'identité et votre justificatif de domiciliation.",
        suite3: "Gardez-en une copie : le greffe peut vous la redemander pendant l'instruction de votre dossier.",
        sanctionTitre: "Ce que vous signez",
        sanction: "Une déclaration inexacte donnée de mauvaise foi en vue d'une immatriculation est punie de {amende} € d'amende et de {mois} mois d'emprisonnement (article L. 123-5 du code de commerce). Relisez les noms et les dates avant d'envoyer.",
        docEnFrancais: "",
    },
    en: {
        phases: ["You", "Your parents", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the document is built there, nobody receives your answers, not even us, and it all disappears with the tab.",
        identiteTitre: "Let us start with your civil status",
        identiteAide: "Use the exact spelling of your birth certificate, accents included.",
        madame: "Ms",
        civilite: "Title",
        monsieur: "Mr",
        civiliteAide: "Used to make the declaration agree, as it is written in the first person.",
        champPrenoms: "Your forenames",
        aidePrenoms: "All of them, in the order shown on your birth certificate.",
        champNom: "Your surname",
        aideNom: "Your birth name, even if you go by a married name.",
        naissanceTitre: "Where and when were you born?",
        naissanceAide: "Together with your parents' names, these two details are used to identify you.",
        champDateNaissance: "Date of birth",
        champVilleNaissance: "Town of birth",
        champPaysNaissance: "Country of birth",
        aidePaysNaissance: "Only needed if you were born outside France.",
        domicileTitre: "Where do you live?",
        domicileAide: "Your home address, not your business address if they differ.",
        champVoie: "Number and street",
        champComplement: "Address line 2",
        aideComplement: "Optional. Building, floor, locality.",
        champCodePostal: "Postcode",
        champCommune: "Town",
        filiationTitre: "Who are your parents?",
        filiationAide: "This is the parentage part of the document. Copy the names as they appear on your birth certificate.",
        pere: "Your father",
        mere: "Your mother",
        champPrenomsParent: "Forenames",
        champNomPere: "Surname",
        champNomMere: "Family name",
        aideNomMere: "Her birth name, not her married name.",
        pereInconnu: "My birth certificate does not name him",
        mereInconnue: "My birth certificate does not name her",
        signatureTitre: "Where are you signing, and when?",
        signatureAide: "The date matters: a declaration dated before your application is fine, the other way round raises questions.",
        champLieuSignature: "Place of signature",
        champDateSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",
        continuer: "Continue",
        voirDocument: "See my document",
        resultatTitre: "Your declaration",
        apercu: "Document preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",
        erreurDateLue: "We read {date}. Please check the year.",
        erreurFormat: "This format is not recognised.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but their content is not accepted. Correct them and come back.",
        motifFormat: "the format is not recognised",
        motifAgeImpossible: "the year gives an impossible age, please check it",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your document.",
        alerteMineur: "That date of birth makes you a minor. The declaration is still possible if you are emancipated, otherwise your application will be refused.",
        suiteTitre: "What happens next",
        suite1: "Print the document, sign it by hand if you did not sign it here, then scan or photograph it.",
        suite2: "Attach it to your registration formality on the French single window, along with your ID and your proof of business address.",
        suite3: "Keep a copy: the registry may ask for it again while your file is being processed.",
        sanctionTitre: "What you are signing",
        sanction: "Giving inaccurate information in bad faith with a view to registration is punishable by a €{amende} fine and {mois} months' imprisonment (article L. 123-5 of the French commercial code). Check the names and dates before you send it.",
        docEnFrancais: "The document below stays in French: it is filed with a French authority, which would refuse a translated version.",
    },
};

const PHASES_PAR_ECRAN = {
    identite: 0,
    naissance: 0,
    domicile: 0,
    filiation: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    civilite: "",
    prenoms: "",
    nom: "",
    dateDeNaissance: "",
    villeDeNaissance: "",
    paysDeNaissance: "",
    voie: "",
    complement: "",
    codePostal: "",
    commune: "",
    perePrenoms: "",
    pereNom: "",
    pereNonDenomme: false,
    merePrenoms: "",
    mereNom: "",
    mereNonDenommee: false,
    lieuDeSignature: "",
    dateDeSignature: "",
};

/** Une case à cocher, pour les deux filiations qu'un acte ne mentionne pas. */
function Case({ libelle, coche, basculer }) {
    return (
        <label className="flex items-center gap-2.5 mt-3 cursor-pointer">
            <input
                type="checkbox"
                checked={coche}
                onChange={(e) => basculer(e.target.checked)}
                className="h-4 w-4 accent-primary cursor-pointer"
            />
            <span className="text-sm text-gray-600 dark:text-lightBlue">{libelle}</span>
        </label>
    );
}

/**
 * Les champs manquants, nommés au lecteur.
 *
 * Hors du dictionnaire `L` : ce sont des clés de la lib, pas des libellés lus
 * dans le JSX, et le contrôle de parité FR/EN les compterait comme jamais lues.
 */
const LIBELLES_MANQUES = {
    fr: {
        civilite: "Votre civilité",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        dateDeNaissance: "Votre date de naissance",
        villeDeNaissance: "Votre commune de naissance",
        voie: "Votre adresse",
        codePostal: "Votre code postal, en cinq chiffres",
        commune: "Votre commune",
        perePrenoms: "Les prénoms de votre père",
        pereNom: "Le nom de votre père",
        merePrenoms: "Les prénoms de votre mère",
        mereNom: "Le nom de famille de votre mère",
        lieuDeSignature: "Le lieu de signature",
    },
    en: {
        civilite: "Your title",
        prenoms: "Your forenames",
        nom: "Your surname",
        dateDeNaissance: "Your date of birth",
        villeDeNaissance: "Your town of birth",
        voie: "Your address",
        codePostal: "Your postcode, five digits",
        commune: "Your town",
        perePrenoms: "Your father's forenames",
        pereNom: "Your father's surname",
        merePrenoms: "Your mother's forenames",
        mereNom: "Your mother's family name",
        lieuDeSignature: "The place of signature",
    },
};

export default function DeclarationNonCondamnation({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    // L'identité gardée sur l'appareil préremplit les champs ; le lecteur
    // relit chaque écran, rien n'est sauté.
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "identite",
        // Recommencer vide les réponses, pas la signature : la garder d'un
        // document à l'autre est tout l'intérêt du pavé, qui porte son propre
        // bouton d'effacement.
        () => {
            // L'identité gardée revient : on recommence le document, pas sa saisie.
            setR({ ...VIDE, ...identite });
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    // L'identité est saisie sur les trois premiers écrans : elle se propose
    // à la garde une fois le domicile passé.
    useSignalerIdentite(
        {
            civilite: r.civilite,
            prenoms: r.prenoms,
            nom: r.nom,
            dateDeNaissance: r.dateDeNaissance,
            villeDeNaissance: r.villeDeNaissance,
            paysDeNaissance: r.paysDeNaissance,
            voie: r.voie,
            complement: r.complement,
            codePostal: r.codePostal,
            commune: r.commune,
        },
        etape === "filiation" || etape === "signature" || etape === "resultat",
    );

    // La date du jour vient de la page, jamais d'un new Date() ici : lue des
    // deux côtés, elle produirait un écart d'hydratation.
    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const declaration = useMemo(
        () => composerDeclaration(reponses, aujourdhui),
        [reponses, aujourdhui],
    );

    /* Chaque écran garde son bouton fermé tant qu'il lui manque une réponse.
       C'est le pattern de la rubrique, et il vaut mieux qu'un résultat qui
       renvoie en arrière : le lecteur voit tout de suite ce qui bloque. */
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());
    const filiationTenue = (prenoms, nom, coche) => coche || rempli(prenoms, nom);

    /* Un champ rempli mais refusé ne se dit pas « il manque » : le lecteur
       relirait dix fois ce qu'il a déjà écrit. */
    const motifLisible = (motif) =>
        motif === "ageImpossible" ? t.motifAgeImpossible : t.motifFormat;


    /* Sous le champ, le refus se dit en phrase, et surtout il MONTRE la date
       comprise : « nous lisons 3 mai 0019 » est plus parlant que « année
       invalide » devant un champ où l'on croit avoir tapé 1962. */
    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (refus.motif === "ageImpossible") {
            return remplir(t.erreurDateLue, { date: dateEnToutesLettres(r[champ]) });
        }
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    const telecharger = () =>
        telechargerDocument({
            document: declaration,
            signature,
            nomFichier: nomDuFichier("declaration-non-condamnation", reponses),
        });

    const copier = async () => {
        if (!declaration) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(declaration));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé par le navigateur : le bouton ne ment pas,
            // il ne se passe rien et le PDF reste la voie normale.
        }
    };

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "identite") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                titre={t.identiteTitre}
                aide={t.identiteAide}
            >
                <div className="space-y-5">
                    <div>
                        <Segments
                            libelle={t.civilite}
                            options={[
                                { cle: "madame", libelle: t.madame },
                                { cle: "monsieur", libelle: t.monsieur },
                            ]}
                            valeur={r.civilite}
                            poser={poser("civilite")}
                        />
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">
                            {t.civiliteAide}
                        </p>
                    </div>
                    <Champ
                        libelle={t.champPrenoms}
                        aide={t.aidePrenoms}
                        valeur={r.prenoms}
                        poser={poser("prenoms")}
                    />
                    <Champ
                        libelle={t.champNom}
                        aide={t.aideNom}
                        valeur={r.nom}
                        poser={poser("nom")}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">
                    {t.prive}
                </p>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("civilite", "prenoms", "nom")}
                    onClick={() => aller("naissance")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "naissance") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.naissanceTitre}
                aide={t.naissanceAide}
            >
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champDateNaissance}
                        valeur={r.dateDeNaissance}
                        poser={poser("dateDeNaissance")}
                        erreur={erreurDe("dateDeNaissance")}
                    />
                    <Champ
                        libelle={t.champVilleNaissance}
                        valeur={r.villeDeNaissance}
                        poser={poser("villeDeNaissance")}
                    />
                    <Champ
                        libelle={t.champPaysNaissance}
                        aide={t.aidePaysNaissance}
                        valeur={r.paysDeNaissance}
                        poser={poser("paysDeNaissance")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("dateDeNaissance", "villeDeNaissance") ||
                        Boolean(refusDe("dateDeNaissance"))
                    }
                    onClick={() => aller("domicile")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "domicile") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={t.domicileTitre}
                aide={t.domicileAide}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champVoie}
                        valeur={r.voie}
                        poser={poser("voie")}
                    />
                    <Champ
                        libelle={t.champComplement}
                        aide={t.aideComplement}
                        valeur={r.complement}
                        poser={poser("complement")}
                    />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ
                            libelle={t.champCodePostal}
                            valeur={r.codePostal}
                            poser={poser("codePostal")}
                            erreur={erreurDe("codePostal")}
                            inputMode="numeric"
                            maxLength={5}
                        />
                        <div className="sm:col-span-2">
                            <Champ
                                libelle={t.champCommune}
                                valeur={r.commune}
                                poser={poser("commune")}
                            />
                        </div>
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("voie", "codePostal", "commune") || Boolean(refusDe("codePostal"))
                    }
                    onClick={() => aller("filiation")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "filiation") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.filiationTitre}
                aide={t.filiationAide}
            >
                <div className="space-y-7">
                    <div>
                        <div className="text-sm font-semibold dark:text-white mb-3">{t.pere}</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Champ
                                libelle={t.champPrenomsParent}
                                valeur={r.perePrenoms}
                                poser={poser("perePrenoms")}
                                disabled={r.pereNonDenomme}
                            />
                            <Champ
                                libelle={t.champNomPere}
                                valeur={r.pereNom}
                                poser={poser("pereNom")}
                                disabled={r.pereNonDenomme}
                            />
                        </div>
                        <Case
                            libelle={t.pereInconnu}
                            coche={r.pereNonDenomme}
                            basculer={poser("pereNonDenomme")}
                        />
                    </div>

                    <div>
                        <div className="text-sm font-semibold dark:text-white mb-3">{t.mere}</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Champ
                                libelle={t.champPrenomsParent}
                                valeur={r.merePrenoms}
                                poser={poser("merePrenoms")}
                                disabled={r.mereNonDenommee}
                            />
                            <Champ
                                libelle={t.champNomMere}
                                aide={t.aideNomMere}
                                valeur={r.mereNom}
                                poser={poser("mereNom")}
                                disabled={r.mereNonDenommee}
                            />
                        </div>
                        <Case
                            libelle={t.mereInconnue}
                            coche={r.mereNonDenommee}
                            basculer={poser("mereNonDenommee")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !filiationTenue("perePrenoms", "pereNom", r.pereNonDenomme) ||
                        !filiationTenue("merePrenoms", "mereNom", r.mereNonDenommee)
                    }
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={2}
                onRetour={retour}
                titre={t.signatureTitre}
                aide={t.signatureAide}
            >
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("lieuDeSignature")}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat
            onRetour={retour}
            retour={t.retour}
            key={etape}
            titre={t.resultatTitre}
            phases={t.phases}
            sens={sens}
            reduit={reduit}
        >
            {!declaration ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li
                                key={cle}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {motifLisible(motif)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("mineur") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {t.alerteMineur}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    {t.docEnFrancais && (
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-3">
                            {t.docEnFrancais}
                        </p>
                    )}

                    <ApercuDocument document={declaration} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon
                                name={copie ? "check-circle-linear" : "copy-linear"}
                                className="text-lg"
                            />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {t.suiteTitre}
                            </div>
                        </div>
                        <ul className="space-y-3">
                            {[t.suite1, t.suite2, t.suite3].map((ligne) => (
                                <li
                                    key={ligne}
                                    className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                                >
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="shield-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">
                                {t.sanctionTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {remplir(t.sanction, {
                                amende: SANCTION.amende.toLocaleString(
                                    lang === "fr" ? "fr-FR" : "en-US",
                                ),
                                mois: SANCTION.moisDEmprisonnement,
                            })}
                        </p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
